import { yupResolver } from "@hookform/resolvers/yup";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Checkbox,
  FormControlLabel,
  Autocomplete,
  TextField,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Card,
  CardContent,
} from "@mui/material";
import { usePatients } from "hooks/usePatients";
import {
  GenerateTreatmentPlanForPatientDto,
  PatientsPaginationRequestDtoStatusEnum,
} from "libs/cellera-sdk";
import React, { useMemo, useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { useCelleraTheme } from "theme/useCelleraTheme";
import { Patient } from "types/patient";
import {
  treatmentProtocolGenerationSchema,
  TreatmentProtocolGenerationFormData,
  LANGUAGE_OPTIONS,
} from "validations";

export interface GenerateProtocolModalProps {
  open: boolean;
  isGenerating: boolean;
  onClose: () => void;
  onGenerate?: (
    params: GenerateTreatmentPlanForPatientDto
  ) => void | Promise<void>;
}

export const GenerateProtocolModal: React.FC<GenerateProtocolModalProps> = ({
  open,
  isGenerating,
  onClose,
  onGenerate,
}) => {
  const { colors } = useCelleraTheme();

  const [isUnderstandChecked, setIsUnderstandChecked] = useState(false);
  const [isPatientProfileUpToDateChecked, setIsPatientProfileUpToDateChecked] =
    useState(false);
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // React Hook Form setup
  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(treatmentProtocolGenerationSchema),
    defaultValues: {
      language: "en",
    },
    mode: "onChange",
    reValidateMode: "onChange",
  });

  // Watch form values for character count display
  const budgetConstraintsValue = watch("budgetConstraints") || "";
  const exclusionsValue = watch("exclusions") || "";
  const clinicalFocusValue = watch("clinicalFocus") || "";

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchInput]);

  // Fetch patients list - only fetch patients with ActiveSharing status
  const { patients, loading: loadingPatients } = usePatients({
    pageSize: 100,
    status: [PatientsPaginationRequestDtoStatusEnum.ActiveSharing],
    search: debouncedSearch || undefined,
  });

  // Use patients directly since API already filters by status
  const patientOptions = useMemo(() => {
    return patients || [];
  }, [patients]);

  const handleGenerate = async (
    formData: TreatmentProtocolGenerationFormData
  ) => {
    if (
      isUnderstandChecked &&
      isPatientProfileUpToDateChecked &&
      selectedPatient
    ) {
      const params: GenerateTreatmentPlanForPatientDto = {
        patientId: selectedPatient.patientId,
        language: formData.language,
      };

      if (formData.budgetConstraints) {
        params.budgetConstraints = formData.budgetConstraints;
      }
      if (formData.exclusions) {
        params.exclusions = formData.exclusions;
      }
      if (formData.clinicalFocus) {
        params.clinicalFocus = formData.clinicalFocus;
      }

      // Call generation logic with patient ID
      await onGenerate?.(params);

      // Close the modal (no navigation)
      onClose();
    }
  };

  const handleClose = () => {
    onClose();
    // Reset state when modal is closed
    setIsUnderstandChecked(false);
    setIsPatientProfileUpToDateChecked(false);
    setSelectedPatient(null);
    setSearchInput("");
    setDebouncedSearch("");
    reset();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Generate Protocol</DialogTitle>
      <DialogContent>
        {/* Patient Selection */}
        <Autocomplete
          options={patientOptions}
          value={selectedPatient}
          onChange={(_, newValue) => setSelectedPatient(newValue)}
          onInputChange={(_, newInputValue) => {
            setSearchInput(newInputValue);
          }}
          inputValue={searchInput}
          getOptionLabel={(option) => {
            if (typeof option === "string") return option;
            return (
              `${option.firstName || ""} ${option.lastName || ""}`.trim() ||
              "Unknown Patient"
            );
          }}
          getOptionKey={(option) => option.patientId}
          loading={loadingPatients}
          filterOptions={(x) => x} // Disable client-side filtering, use server-side search
          noOptionsText={
            loadingPatients
              ? "Loading..."
              : debouncedSearch
                ? "No patients found"
                : "Type to search for a patient"
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Select Patient *"
              placeholder="Search for a patient"
              fullWidth
              margin="dense"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loadingPatients ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />

        {/* Language */}
        <Controller
          name="language"
          control={control}
          render={({ field }) => (
            <FormControl fullWidth margin="dense">
              <InputLabel id="language-label">Language</InputLabel>
              <Select
                labelId="language-label"
                id="language"
                label="Language"
                margin="dense"
                error={!!errors.language}
                fullWidth
                {...field}
              >
                {LANGUAGE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        />

        {/* Budget Constraints */}
        <Controller
          name="budgetConstraints"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label="Budget Constraints (Optional)"
              placeholder="You can specify the financial cap to instruct the system on which treatments to include or omit, ensuring the protocol aligns with the patient's pricing preferences"
              fullWidth
              slotProps={{ htmlInput: { maxLength: 250 } }}
              multiline
              rows={2}
              margin="dense"
              helperText={
                errors.budgetConstraints?.message ||
                `${budgetConstraintsValue.length}/250 characters`
              }
              error={!!errors.budgetConstraints}
            />
          )}
        />

        {/* Exclusions */}
        <Controller
          name="exclusions"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label="Exclusions (Optional)"
              placeholder="You can specify what treatments must be excluded to respect patient preferences (e.g., 'No IV treatments' or 'No hormone therapies')"
              fullWidth
              multiline
              rows={4}
              margin="dense"
              slotProps={{ htmlInput: { maxLength: 2000 } }}
              helperText={
                errors.exclusions?.message ||
                `${exclusionsValue.length}/2000 characters`
              }
              error={!!errors.exclusions}
            />
          )}
        />

        {/* Clinical Focus */}
        <Controller
          name="clinicalFocus"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label="Clinical Focus (Optional)"
              placeholder="You can specify the priority inputs—such as 'Fat Loss' or 'Hair Restoration'—to guide the AI in emphasizing these goals within the generated protocol"
              fullWidth
              multiline
              rows={4}
              margin="dense"
              slotProps={{ htmlInput: { maxLength: 2000 } }}
              helperText={
                errors.clinicalFocus?.message ||
                `${clinicalFocusValue.length}/2000 characters`
              }
              error={!!errors.clinicalFocus}
            />
          )}
        />

        {/* Legal Disclaimer */}
        <Card
          variant="outlined"
          sx={{ borderColor: colors.border.light, mb: 2 }}
        >
          <CardContent>
            <Typography variant="body1" paragraph>
              This action will legally bind the staff member to the information
              within the generated report, thereby assuming legal liability.
              This indispensable step establishes the plan as a physician-guided
              protocol, not an AI-generated one.
            </Typography>

            <FormControlLabel
              control={
                <Checkbox
                  checked={isUnderstandChecked}
                  onChange={(e) => setIsUnderstandChecked(e.target.checked)}
                />
              }
              label="I understand"
              sx={{ ml: 0.5 }}
            />
          </CardContent>
        </Card>

        <Card variant="outlined" sx={{ borderColor: colors.border.light }}>
          <CardContent>
            <Typography variant="body1" paragraph>
              In order to get the most suited personalized wellness protocol,
              the user needs to make sure that patient has all their data to be
              up-to-date and most accurate.
            </Typography>

            <FormControlLabel
              control={
                <Checkbox
                  checked={isPatientProfileUpToDateChecked}
                  onChange={(e) =>
                    setIsPatientProfileUpToDateChecked(e.target.checked)
                  }
                />
              }
              label="Patient profile data are all up-to-date"
              sx={{ ml: 0.5 }}
            />
          </CardContent>
        </Card>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="secondary">
          Cancel
        </Button>
        <Button
          onClick={handleSubmit(handleGenerate)}
          color="primary"
          variant="contained"
          disabled={
            !isUnderstandChecked ||
            !isPatientProfileUpToDateChecked ||
            !selectedPatient ||
            isGenerating
          }
        >
          Generate Protocol
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GenerateProtocolModal;
